// Single suggestion in the trainer review tray. Renders the base vs
// suggested text as a two-column line diff and exposes Accept / Reject /
// Merge actions. Merge expands an inline editor pre-filled with the
// suggested text so a trainer can hand-edit before publishing.

import { useMemo, useState } from "react";
import { diffLines } from "@/lib/aiPrompting/diff";
import { type SuggestionDTO, type DecideArgs } from "@/lib/aiPrompting/client";

export type SuggestionDiffRowProps = {
  suggestion: SuggestionDTO;
  onDecide: (args: DecideArgs) => Promise<{ ok: true } | { ok: false; error: string }>;
};

export function SuggestionDiffRow({ suggestion, onDecide }: SuggestionDiffRowProps) {
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeText, setMergeText] = useState(suggestion.suggested_prompt_text);
  const [decisionNote, setDecisionNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(
    () => diffLines(suggestion.base_prompt_text, suggestion.suggested_prompt_text),
    [suggestion.base_prompt_text, suggestion.suggested_prompt_text],
  );

  const submit = async (action: DecideArgs["action"]) => {
    setBusy(true);
    setError(null);
    const r = await onDecide({
      id: suggestion.id,
      action,
      decisionNote: decisionNote.trim() ? decisionNote.trim() : null,
      mergedText: action === "merge" ? mergeText : null,
    });
    setBusy(false);
    if (!r.ok) setError(r.error);
  };

  return (
    <li className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-700 dark:bg-amber-950/30">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-100">
            <code className="font-mono">{suggestion.prompt_key}</code>{" "}
            <span className="font-normal text-amber-800/70 dark:text-amber-200/70">
              · {new Date(suggestion.created_at).toLocaleString()}
            </span>
          </p>
          {suggestion.note ? (
            <p className="mt-1 text-xs text-amber-900/90 dark:text-amber-100/90">
              <span className="font-semibold">Note:</span> {suggestion.note}
            </p>
          ) : null}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
        <DiffColumn label="Current" rows={rows.map((r) => r.base)} kinds={rows.map((r) => r.kind)} side="left" />
        <DiffColumn
          label="Suggested"
          rows={rows.map((r) => r.suggested)}
          kinds={rows.map((r) => r.kind)}
          side="right"
        />
      </div>

      {mergeOpen ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={mergeText}
            onChange={(e) => setMergeText(e.target.value)}
            spellCheck={false}
            rows={Math.min(18, Math.max(6, mergeText.split("\n").length + 1))}
            className="w-full rounded-lg border border-violet-200 bg-white p-2 font-mono text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-300 dark:border-zinc-700 dark:bg-zinc-900/60 dark:text-zinc-100"
          />
        </div>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={decisionNote}
          onChange={(e) => setDecisionNote(e.target.value)}
          placeholder="Optional decision note"
          className="min-w-[180px] flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
        />
        <button
          type="button"
          onClick={() => void submit("accept")}
          disabled={busy}
          className="text-xs rounded-md bg-emerald-700 px-2.5 py-1 font-semibold text-white hover:bg-emerald-800 disabled:opacity-50 dark:bg-emerald-600 dark:hover:bg-emerald-500"
        >
          Accept
        </button>
        <button
          type="button"
          onClick={() => setMergeOpen((open) => !open)}
          disabled={busy}
          className="text-xs rounded-md border border-violet-300 bg-violet-50 px-2.5 py-1 font-semibold text-violet-900 hover:bg-violet-100 disabled:opacity-50 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-100"
        >
          {mergeOpen ? "Cancel merge" : "Merge…"}
        </button>
        {mergeOpen ? (
          <button
            type="button"
            onClick={() => void submit("merge")}
            disabled={busy}
            className="text-xs rounded-md bg-violet-700 px-2.5 py-1 font-semibold text-white hover:bg-violet-800 disabled:opacity-50 dark:bg-violet-600 dark:hover:bg-violet-500"
          >
            Apply merged
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => void submit("reject")}
          disabled={busy}
          className="text-xs rounded-md border border-red-300 bg-red-50 px-2.5 py-1 font-semibold text-red-900 hover:bg-red-100 disabled:opacity-50 dark:border-red-700 dark:bg-red-950/40 dark:text-red-100"
        >
          Reject
        </button>
      </div>
      {error ? <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p> : null}
    </li>
  );
}

type DiffKind = "same" | "removed" | "added";

function DiffColumn({
  label,
  rows,
  kinds,
  side,
}: {
  label: string;
  rows: Array<string | null>;
  kinds: DiffKind[];
  side: "left" | "right";
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-amber-100 bg-white dark:border-zinc-700 dark:bg-zinc-900/50">
      <div className="border-b border-amber-100 bg-amber-50/50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-amber-100">
        {label}
      </div>
      <div className="max-h-72 overflow-y-auto font-mono text-[11px] leading-relaxed">
        {rows.map((line, idx) => (
          <div
            key={`${side}:${idx}`}
            className={
              "whitespace-pre-wrap px-2 py-0.5 " +
              (kinds[idx] === "same"
                ? "text-gray-700 dark:text-zinc-300"
                : side === "left" && kinds[idx] === "removed"
                  ? "bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-100"
                  : side === "right" && kinds[idx] === "added"
                    ? "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100"
                    : "text-gray-300 dark:text-zinc-600")
            }
          >
            {line ?? " "}
          </div>
        ))}
      </div>
    </div>
  );
}
